/**
 * DAF-19 — composite report-section structural schemas.
 *
 * Cross-cutting rollup sections (exec summary, constructability, cost,
 * risk matrix, recommendations graph) are assembled from upstream
 * report slices. These schemas give H1 drift + J3 full-audit structural
 * checks a canonical contract instead of treating the sections as
 * prose-only.
 */
export interface CompositeSectionStructuralRules {
  minWordCount?: number;
  maxWordCount?: number;
  requiresTable?: boolean;
  requiresCallout?: boolean;
  citationDensityMin?: number;
}

export interface CompositeSectionSchema {
  sectionId: string;
  canonicalHeadings: string[];
  requiredSubsections: string[];
  optionalSubsections: string[];
  structuralRules: CompositeSectionStructuralRules;
}

export const COMPOSITE_SECTION_IDS = [
  'execSummary',
  'constructabilityRollup',
  'costRangeRollup',
  'riskMatrixRollup',
  'recommendationsRollup',
] as const;

export type CompositeSectionId = typeof COMPOSITE_SECTION_IDS[number];

/** Alias used by audit + drift modules (DAF-19). */
export const SECTION_SCHEMAS: Record<CompositeSectionId, CompositeSectionSchema> = {
  execSummary: {
    sectionId: 'execSummary',
    canonicalHeadings: ['Top Findings', 'Verdict', 'Strategic Recommendation'],
    requiredSubsections: ['top-findings', 'verdict'],
    optionalSubsections: ['strategic-recommendation', 'investment-thesis'],
    structuralRules: {
      minWordCount: 600,
      requiresCallout: true,
      citationDensityMin: 0.85,
    },
  },
  constructabilityRollup: {
    sectionId: 'constructabilityRollup',
    canonicalHeadings: ['Site Readiness', 'Construction Constraints', 'Phasing'],
    requiredSubsections: ['site-readiness', 'construction-constraints'],
    optionalSubsections: ['phasing', 'grading-setbacks'],
    structuralRules: {
      minWordCount: 400,
      requiresTable: true,
    },
  },
  costRangeRollup: {
    sectionId: 'costRangeRollup',
    canonicalHeadings: ['CAPEX Range', 'Cost Drivers', 'Sensitivity'],
    requiredSubsections: ['capex-range', 'cost-drivers'],
    optionalSubsections: ['sensitivity', 'contingency'],
    structuralRules: {
      minWordCount: 350,
      requiresTable: true,
      citationDensityMin: 0.7,
    },
  },
  riskMatrixRollup: {
    sectionId: 'riskMatrixRollup',
    canonicalHeadings: ['Risk Matrix', 'Severity × Likelihood', 'Mitigations'],
    requiredSubsections: ['risk-matrix', 'mitigations'],
    optionalSubsections: ['severity-likelihood', 'residual-risk'],
    structuralRules: {
      minWordCount: 300,
      requiresTable: true,
    },
  },
  recommendationsRollup: {
    sectionId: 'recommendationsRollup',
    canonicalHeadings: ['Recommendations', 'Priority Actions', 'Dependencies'],
    requiredSubsections: ['recommendations', 'priority-actions'],
    optionalSubsections: ['dependencies', 'next-steps'],
    structuralRules: {
      minWordCount: 250,
      requiresCallout: true,
    },
  },
};

export function getCompositeSectionSchema(sectionId: string): CompositeSectionSchema | undefined {
  return SECTION_SCHEMAS[sectionId as CompositeSectionId];
}

export function listCompositeSectionSchemas(): CompositeSectionSchema[] {
  return COMPOSITE_SECTION_IDS.map((id) => SECTION_SCHEMAS[id]);
}
